import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { toast } from 'sonner';

import { http, setAdminAccessToken, setPortalAccessToken } from '@/lib/http/apiFetcher';
import { AuthContext } from '@/lib/auth/AuthContext';
import { PortalAuthContext } from '@/lib/portal-auth/PortalAuthContext';
import { setCurrentMode, type OperationMode } from './setCurrentMode';

/**
 * Bitflag-style entitlement string mirroring the backend's
 * `AllowedEnvironments` enum. We accept the string form rather than the raw
 * bitflag because the wire shape is `"None" | "Sandbox" | "Production" |
 * "Both"`.
 */
export type AllowedEnvironments = 'None' | 'Sandbox' | 'Production' | 'Both';

export interface ModeContextValue {
  /** Active operation mode in this tab. Persisted to localStorage. */
  mode: OperationMode;
  /**
   * Request a switch. Calls the appropriate `/auth/.../refresh?mode=...`
   * endpoint, updates the auth context tokens, persists the new mode, and
   * fires a sonner toast on success/failure. No-op if `next === mode`.
   */
  setMode: (next: OperationMode) => Promise<void>;
  /** Entitlement string from `/auth/.../me` — defaults to `'Both'` for admin
   *  and `'Production'` for portal until Phase B ships the field. */
  allowed: AllowedEnvironments;
  /** True while a switch round-trip is in flight; disable the switcher UI. */
  isSwitching: boolean;
}

// Exported so tests can render against a stub ModeContext without spinning
// up the full provider (which talks to localStorage + the refresh endpoint).
export const ModeContext = createContext<ModeContextValue | null>(null);

const STORAGE_KEY = 'dwpay.mode';

/**
 * Auto-dismiss windows for the post-switch toast. Success is brief —
 * the user already SAW the env chip + banner flip, so the toast is
 * just a confirmation receipt. Errors linger longer so the user has
 * time to read them.
 *
 * Sonner accepts these as the `duration` field; we pass them explicitly
 * (rather than relying on the global `<Toaster>` default) so the values
 * are obvious at the call site and easy to tune without hunting through
 * provider config.
 */
const TOAST_SUCCESS_DURATION = 2500;
const TOAST_ERROR_DURATION = 5000;

function readStoredMode(): OperationMode {
  // SSR-safe — defaults if window is missing (shouldn't happen in this SPA,
  // but cheap insurance for test environments that bootstrap without jsdom).
  if (typeof window === 'undefined') return 'production';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'sandbox' || raw === 'production') return raw;
  } catch {
    /* localStorage may be blocked (private window); fall through. */
  }
  return 'production';
}

function writeStoredMode(mode: OperationMode): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* ignore — quota / privacy mode */
  }
}

function applyDomAttribute(mode: OperationMode): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.mode = mode;
}

/**
 * `allowedEnvironments` is a Phase-B addition to `/auth/.../me`. Code
 * defensively: an admin response that's missing the field is treated as
 * `'Both'` (don't lock anyone out before the backend wires it up); a portal
 * response is treated as `'Production'` (sandbox is opt-in for client users).
 */
function readAdminAllowed(user: unknown): AllowedEnvironments {
  if (user && typeof user === 'object') {
    const v = (user as { allowedEnvironments?: unknown }).allowedEnvironments;
    if (v === 'None' || v === 'Sandbox' || v === 'Production' || v === 'Both') return v;
  }
  return 'Both';
}

function readPortalAllowed(user: unknown): AllowedEnvironments {
  if (user && typeof user === 'object') {
    const v = (user as { allowedEnvironments?: unknown }).allowedEnvironments;
    if (v === 'None' || v === 'Sandbox' || v === 'Production' || v === 'Both') return v;
  }
  return 'Production';
}

export function isModeAllowed(allowed: AllowedEnvironments, mode: OperationMode): boolean {
  if (allowed === 'Both') return true;
  if (allowed === 'None') return false;
  if (mode === 'sandbox') return allowed === 'Sandbox';
  return allowed === 'Production';
}

interface RefreshApiResponse {
  accessToken?: string;
  accessExpiresAt?: string;
  user?: { id: string; email: string };
}

interface ErrorPayload {
  proxyErrorCode?: string;
  detail?: string;
}

function extractProxyErrorCode(err: unknown): string | undefined {
  const status = (err as { response?: { status?: number; data?: ErrorPayload } }).response;
  return status?.data?.proxyErrorCode;
}

/**
 * Mounts above the rest of the app. Wraps the two auth contexts so it can
 * read the current user's entitlement and call the right refresh endpoint
 * when the user toggles the switcher. Mirrors next-themes' shape — DOM
 * attribute + localStorage persistence.
 */
export function ModeProvider({ children }: { children: ReactNode }) {
  // Read both contexts directly via useContext so we degrade gracefully when
  // one isn't mounted (e.g. a test that exercises only the admin layout
  // doesn't have a PortalAuthProvider in the tree). The throwing `useAuth`
  // / `usePortalAuth` hooks are caller-facing convenience wrappers and we
  // intentionally bypass them here.
  const auth = useContext(AuthContext);
  const portalAuth = useContext(PortalAuthContext);

  const [mode, setModeState] = useState<OperationMode>(() => readStoredMode());
  const [isSwitching, setIsSwitching] = useState(false);

  // Bring the module-level setter + DOM attribute in sync on mount. The
  // hooks consumers can rely on `data-mode` being present before paint.
  useEffect(() => {
    setCurrentMode(mode);
    applyDomAttribute(mode);
    writeStoredMode(mode);
  }, [mode]);

  // Pick the right entitlement — admin context wins when admin is signed in,
  // otherwise portal. If neither is signed in, fall back to the safe
  // defaults (Both for admin layouts, Production for portal layouts).
  const allowed = useMemo<AllowedEnvironments>(() => {
    if (auth?.user) return readAdminAllowed(auth.user);
    if (portalAuth?.user) return readPortalAllowed(portalAuth.user);
    return 'Both';
  }, [auth?.user, portalAuth?.user]);

  const setMode = useCallback(
    async (next: OperationMode): Promise<void> => {
      if (next === mode) return;
      if (!isModeAllowed(allowed, next)) {
        toast.error(`You don't have access to ${next} mode.`, { duration: TOAST_ERROR_DURATION });
        return;
      }

      // Pick the endpoint based on who's signed in. If both contexts are
      // populated (rare — admin + portal in the same tab), prefer the
      // surface the current URL belongs to.
      //
      // We POST to `/switch-mode` (Bearer-authenticated) rather than
      // `/refresh?mode=...` (refresh-cookie-authenticated). The httpOnly
      // `pgp_*_refresh` cookie occasionally doesn't reach the server through
      // some reverse-proxy + SameSite=Strict combinations on POST,
      // producing a stray "Refresh cookie missing" 401. The switch-mode
      // endpoint uses the access token in the Authorization header (which
      // the axios interceptor stamps on every request), and the backend
      // rotates the refresh session if the cookie HAPPENS to be present —
      // making mode-switch reliable regardless of cookie behavior.
      const isPortal =
        typeof window !== 'undefined' && window.location.pathname.startsWith('/portal');
      const switchUrl = isPortal ? '/auth/portal/switch-mode' : '/auth/admin/switch-mode';

      setIsSwitching(true);
      try {
        const { data } = await http.post<RefreshApiResponse>(switchUrl, { mode: next });
        // Update the right access-token slot so subsequent requests carry
        // the new env-bound JWT. Code defensively — Phase B will populate
        // these; today the backend ignores the mode param and returns the
        // same env, but the token rotate still works.
        if (data.accessToken) {
          if (isPortal) setPortalAccessToken(data.accessToken);
          else setAdminAccessToken(data.accessToken);
        }
        setModeState(next);
        toast.success(`Switched to ${next} mode.`, { duration: TOAST_SUCCESS_DURATION });
      } catch (err) {
        const code = extractProxyErrorCode(err);
        if (code === 'auth.environment_forbidden') {
          toast.error(`You don't have access to ${next} mode.`, { duration: TOAST_ERROR_DURATION });
        } else {
          toast.error(`Could not switch to ${next} mode.`, { duration: TOAST_ERROR_DURATION });
        }
        // Don't change state on failure — the chip + banner stay where they
        // were so the user isn't dropped into a half-switched limbo.
      } finally {
        setIsSwitching(false);
      }
    },
    [mode, allowed],
  );

  const value = useMemo<ModeContextValue>(
    () => ({ mode, setMode, allowed, isSwitching }),
    [mode, setMode, allowed, isSwitching],
  );

  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
}

export function useMode(): ModeContextValue {
  const ctx = useContext(ModeContext);
  if (!ctx) {
    throw new Error('useMode() called without <ModeProvider /> in the tree.');
  }
  return ctx;
}

// Re-export the module-level helpers so callers outside React (axios
// interceptor, background tasks) can share the same source of truth without
// pulling in the provider file.
export { getCurrentMode, setCurrentMode } from './setCurrentMode';
export type { OperationMode } from './setCurrentMode';
